import type { ReactNode } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import {
  Button,
  Card,
  EmptyState,
  Table,
  type Column,
} from "@marekl/adminer/components";
import type { ID } from "../../../lib/id";

/**
 * One component of a rule set — an age table, a formation table, a
 * deduction table, a scoring sheet or the list of competition surfaces —
 * rendered as a card with its rows beneath it.
 *
 * They have the same shape (a named heading plus ordered rows) and the
 * same affordances, so they share this shell; only the columns and the
 * edit form differ, and those stay in the tabs.
 *
 * onEditTable / onDeleteTable are optional: the floors are a flat list
 * under the rule set with no container of their own, so that card is a
 * heading rather than an editable row.
 */
export interface ComponentCardProps<Row extends { id: ID }> {
  title: string;
  subtitle?: ReactNode;
  /** Archived rule sets hide every mutating control. */
  readOnly: boolean;
  /**
   * Hide the row-level „Smazat" only, keeping edit and reorder live.
   *
   * Narrower than `readOnly` on purpose: a rule set in service freezes
   * WHICH rows exist — each one is a rung of the combination matrix — while
   * what they say stays editable. `readOnly` drops the whole actions column
   * and would take „Upravit" and the reorder arrows with it.
   *
   * Adding is deliberately still offered: widening the matrix orphans no
   * přihláška, which is the same asymmetry the backend applies.
   */
  lockDelete?: boolean;
  rows: Row[];
  columns: Column<Row>[];
  emptyTitle: string;
  emptyDescription: string;
  addLabel: string;
  onEditTable?: () => void;
  onDuplicateTable?: () => void;
  onDeleteTable?: () => void;
  onAddRow: () => void;
  onEditRow: (row: Row) => void;
  onDeleteRow: (row: Row) => void;
  onMoveRow: (index: number, direction: -1 | 1) => void;
}

export function ComponentCard<Row extends { id: ID }>({
  title,
  subtitle,
  readOnly,
  lockDelete,
  rows,
  columns,
  emptyTitle,
  emptyDescription,
  addLabel,
  onEditTable,
  onDuplicateTable,
  onDeleteTable,
  onAddRow,
  onEditRow,
  onDeleteRow,
  onMoveRow,
}: ComponentCardProps<Row>) {
  const allColumns: Column<Row>[] = readOnly
    ? columns
    : [
        ...columns,
        {
          key: "actions",
          header: "",
          render: (row: Row) => {
            const index = rows.findIndex((r) => r.id === row.id);
            return (
              <Table.Actions>
                <Table.Action
                  appearance="button"
                  iconOnly
                  aria-label="Posunout nahoru"
                  disabled={index === 0}
                  onClick={() => onMoveRow(index, -1)}
                >
                  <ChevronUp aria-hidden="true" />
                </Table.Action>
                <Table.Action
                  appearance="button"
                  iconOnly
                  aria-label="Posunout dolů"
                  disabled={index === rows.length - 1}
                  onClick={() => onMoveRow(index, 1)}
                >
                  <ChevronDown aria-hidden="true" />
                </Table.Action>
                <Table.Action appearance="button" onClick={() => onEditRow(row)}>Upravit</Table.Action>
                {!lockDelete && (
                  <Table.Action appearance="button" variant="danger" onClick={() => onDeleteRow(row)}>
                    Smazat
                  </Table.Action>
                )}
              </Table.Actions>
            );
          },
        },
      ];

  return (
    <Card>
      <Card.Header
        title={title}
        subtitle={subtitle}
        subtitlePlacement="right"
        actions={
          readOnly || !onEditTable || !onDeleteTable ? undefined : (
            <>
              <Button size="sm" onClick={onEditTable}>
                Upravit
              </Button>
              {onDuplicateTable && (
                <Button size="sm" onClick={onDuplicateTable}>
                  Duplikovat
                </Button>
              )}
              {/* Outline: this repeats on every component card, and it
                  opens a confirm rather than deleting anything itself. */}
              <Button size="sm" variant="danger" weight="outline" onClick={onDeleteTable}>
                Smazat
              </Button>
            </>
          )
        }
      />
      <Card.Content padding="none">
        {/* The empty state goes through Table's own slot rather than a
            ternary beside it. Not for the usual reason — this card is handed
            `rows` and never sees a failure, because RuleSetDetailPage renders
            an ErrorState instead of the tabs — but for the visible one: the
            slot keeps the column headers on screen, so an empty srážková
            tabulka still shows what it would have contained. The ternary
            replaced the whole table with a bare sentence. */}
        <Table<Row>
          rows={rows}
          rowKey={(r) => r.id}
          columns={allColumns}
          dense
          empty={<EmptyState title={emptyTitle} description={emptyDescription} />}
        />
      </Card.Content>
      {!readOnly && (
        <Card.Footer>
          <Button size="sm" onClick={onAddRow}>
            {addLabel}
          </Button>
        </Card.Footer>
      )}
    </Card>
  );
}
